import {
  CreditCard as BillingIcon,
  CloudSync as CdnIcon,
  Settings as GeneralIcon,
  SafetyDivider as GuardrailsIcon,
  People as MembersIcon,
  Lock as RolesIcon,
  Web as SitesIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import { LEGACY_API_VERSION } from '@takeshape/schema';
import { equals, falsy, not } from '@takeshape/util';
import EntitlementsWrapper from 'app/common/components/entitlements-wrapper';
import Tag from 'app/common/components/tag';
import EntitlementIcon from 'app/common/icons/entitlement';
import WebhookIcon from 'app/common/icons/webhook';
import WorkflowIcon from 'app/common/icons/workflow';
import PermissionsWrapper from 'app/permissions/components/permissions-wrapper';
import { getResourceString, PROJECT_RESOURCE } from 'app/permissions/util';
import ApiVersionWrapper from 'app/project/components/api-version-wrapper';
import type React from 'react';
import NavItemLink from '../../nav-item-link/index.js';
import ShapeListPermissionsWrapper from '../../shape-list-permissions-wrapper.js';
import type { SettingsNavProps } from './settings-nav-hook.js';

/** Provides navigation options on settings-related routes. */
export const SettingsNav: React.FC<SettingsNavProps> = (props) => {
  const { project, projectPath, branchPath, showBilling, close } = props;
  const billingStatus = project?.status?.billing?.subscriptionStatus ?? '';
  const unpaid = ['unpaid', 'canceled', 'past_due'].includes(billingStatus);

  return (
    <ul data-testid="settings-nav">
      <PermissionsWrapper action="project:update" resource={PROJECT_RESOURCE}>
        <NavItemLink
          href={`${projectPath}/settings`}
          text="General"
          icon={<GeneralIcon fontSize="small" />}
          onClick={close}
        />
      </PermissionsWrapper>
      <PermissionsWrapper action="api-key:list" resource={getResourceString('api-key')}>
        <NavItemLink
          href={`${projectPath}/api-keys`}
          text="API Keys"
          icon={<VpnKeyIcon fontSize="small" />}
          onClick={close}
        />
      </PermissionsWrapper>
      <PermissionsWrapper action="webhook:get" resource={getResourceString('webhook')}>
        <NavItemLink
          href={`${projectPath}/webhooks`}
          text="Webhooks"
          icon={<WebhookIcon fontSize="small" />}
          rightSideItems={[
            {
              element: (
                <EntitlementsWrapper predicate={falsy} entitlement="webhooks">
                  <EntitlementIcon fontSize="small" />
                </EntitlementsWrapper>
              ),
              alwaysVisible: true
            }
          ]}
          onClick={close}
        />
      </PermissionsWrapper>
      <EntitlementsWrapper entitlement="staticSites">
        <ShapeListPermissionsWrapper shapeName="TSStaticSite">
          <NavItemLink
            href={`${branchPath}/sites`}
            text="Static Sites"
            icon={<SitesIcon fontSize="small" onClick={close} />}
          />
        </ShapeListPermissionsWrapper>
      </EntitlementsWrapper>
      <ApiVersionWrapper predicate={not(equals(LEGACY_API_VERSION))}>
        <PermissionsWrapper action="workflow:create" resource={getResourceString('workflow')}>
          <NavItemLink
            href={`${branchPath}/workflows`}
            text="Workflows"
            icon={<WorkflowIcon fontSize="small" />}
            rightSideItems={[
              {
                element: (
                  <EntitlementsWrapper predicate={falsy} entitlement="workflows">
                    <EntitlementIcon fontSize="small" />
                  </EntitlementsWrapper>
                ),
                alwaysVisible: true
              }
            ]}
            onClick={close}
          />
        </PermissionsWrapper>
      </ApiVersionWrapper>
      <PermissionsWrapper action="guardrails:listGuards" resource={getResourceString('guardrails')}>
        <NavItemLink
          href={`${branchPath}/guardrails`}
          text="Guardrails"
          icon={<GuardrailsIcon fontSize="small" />}
          rightSideItems={[
            {
              element: (
                <EntitlementsWrapper predicate={falsy} entitlement="guards">
                  <EntitlementIcon fontSize="small" />
                </EntitlementsWrapper>
              ),
              alwaysVisible: true
            }
          ]}
          onClick={close}
        />
      </PermissionsWrapper>
      <EntitlementsWrapper entitlement="apiCdn">
        <PermissionsWrapper action="cache:clear" resource="ts:cache:{projectId}">
          <NavItemLink href={`${projectPath}/cdn`} text="CDN" icon={<CdnIcon fontSize="small" />} />
        </PermissionsWrapper>
      </EntitlementsWrapper>
      <PermissionsWrapper action="role:list" resource={getResourceString('role')}>
        <NavItemLink
          href={`${projectPath}/roles`}
          text="Roles"
          icon={<RolesIcon fontSize="small" />}
          rightSideItems={[
            {
              element: (
                <EntitlementsWrapper predicate={falsy} entitlement="roles">
                  <EntitlementIcon fontSize="small" />
                </EntitlementsWrapper>
              ),
              alwaysVisible: true
            }
          ]}
          onClick={close}
        />
      </PermissionsWrapper>
      <PermissionsWrapper action="project-member:list" resource={getResourceString('project-member')}>
        <NavItemLink
          href={`${projectPath}/members`}
          text="Members"
          icon={<MembersIcon fontSize="small" onClick={close} />}
          onClick={close}
        />
      </PermissionsWrapper>
      {showBilling && (
        <PermissionsWrapper action="project-billing:get" resource={getResourceString('project-billing')}>
          <NavItemLink
            href={`${projectPath}/billing`}
            text="Billing"
            icon={<BillingIcon fontSize="small" />}
            rightSideItems={
              unpaid
                ? [
                    {
                      element: <Tag status="warning" value="PAST DUE" />,
                      alwaysVisible: true
                    }
                  ]
                : []
            }
            onClick={close}
          />
        </PermissionsWrapper>
      )}
    </ul>
  );
};

export default SettingsNav;
